<!-- src/components/Applications/AfsalSereign/Steps/Step3.vue -->
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import SSNInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
  changeSteps?: () => void // optional: if your wrapper wants to react on change
}>()

// Validation rules
const rules = {
  umsokn: {
    spouseName: { required },
    spouseSSN: { required },
    spouseAddress: { required },
    spousePostalCode: { required },
    spouseEmail: { required, email },
    spousePhone: { required },
  },
}
const v$ = useVuelidate(rules, { umsokn })

function onChange() {
  props.changeSteps?.()
}

// Called by wrapper before advancing
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}

defineExpose({ validate })
</script>

<template>
  <div v-if="umsokn">
    <v-col :class="!props.disable ? 'mx-auto' : ''">
      <v-row>
        <v-col>
          <h2 class="forms__header">
            {{ t('umsokn_afsal_sereign_step3_header1') }}
          </h2>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.spouseName"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_name')"
            :placeholder="t('umsokn_afsal_sereign_step3_name')"
            variant="outlined"
            density="comfortable"
            type="text"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.spouseName.$error">
            {{ t('umsokn_umsaekjandi_nafn_error') }}
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <SSNInput
            v-model="umsokn.spouseSSN"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_kennitala')"
            :placeholder="t('umsokn_afsal_sereign_step3_kennitala')"
            variant="outlined"
            density="comfortable"
            type="text"
            inputmode="numeric"
            pattern="[0-9\-]*"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.spouseSSN.$error">
            {{ t('umsokn_umsaekjandi_kennitala_error') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.spouseAddress"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_heimilisfang')"
            :placeholder="t('umsokn_afsal_sereign_step3_heimilisfang')"
            variant="outlined"
            density="comfortable"
            type="text"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.spouseAddress.$error">
            {{ t('umsokn_umsaekjandi_heimilisfang_error') }}
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.spousePostalCode"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_postnumer')"
            :placeholder="t('umsokn_afsal_sereign_step3_postnumer')"
            variant="outlined"
            density="comfortable"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.spousePostalCode.$error">
            {{ t('umsokn_umsaekjandi_postnumer_error') }}
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.spouseEmail"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_netfang')"
            :placeholder="t('umsokn_afsal_sereign_step3_netfang')"
            variant="outlined"
            density="comfortable"
            type="email"
            :error="v$.umsokn.spouseEmail.$dirty && v$.umsokn.spouseEmail.$error"
            :error-messages="[
              v$.umsokn.spouseEmail.required?.$invalid ? t('umsokn_umsaekjandi_netfang_error') : '',
              v$.umsokn.spouseEmail.email?.$invalid ? t('umsokn_umsaekjandi_netfang_invalid') : ''
            ].filter(Boolean)"
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.spousePhone"
            :readonly="props.disable"
            :label="t('umsokn_afsal_sereign_step3_simanumer')"
            :placeholder="t('umsokn_afsal_sereign_step3_simanumer')"
            variant="outlined"
            density="comfortable"
            type="tel"
            inputmode="tel"
            pattern="[0-9 ]*"
            @update:model-value="onChange"
          />
          <div class="error__text" v-if="v$.umsokn.spousePhone.$error">
            {{ t('umsokn_umsaekjandi_simanumer_error') }}
          </div>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
.error__text {
  color: #b00020; /* Vuetify default error color */
  font-size: 0.875rem;
  margin-top: 4px;
}
</style>
